import { schemas, SKILL_DOMAIN_DISPLAY_NAMES } from '@oneappexchange/oaf-schema';
import type { SkillDomain } from '@oneappexchange/oaf-schema';
import type { SiteDomain } from '~/modules/appx_site_schema';
import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { isSkillsLandingPageAvailable } from '~/modules/utilities/externalAgentExperience';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';

/** Technology values accepted by the External Agents search contract. */
export const EXTERNAL_AGENT_TECHNOLOGY_SKILL = 'ai-skill' as const;
export const EXTERNAL_AGENT_TECHNOLOGY_PLUGIN = 'ai-plugin' as const;
export const EXTERNAL_AGENT_TECHNOLOGIES = [EXTERNAL_AGENT_TECHNOLOGY_SKILL, EXTERNAL_AGENT_TECHNOLOGY_PLUGIN] as const;

export const isPluginOnlyExternalAgentTechnology = (technologies: string[] | undefined): boolean => {
    const distinctTechnologies = new Set(technologies);
    return distinctTechnologies.size === 1 && distinctTechnologies.has(EXTERNAL_AGENT_TECHNOLOGY_PLUGIN);
};

/** Canonical filter values come directly from the same oAF schema used by ingestion and search. */
export const SKILL_DOMAINS = schemas['listing/SkillDomain'].get().enum as readonly SkillDomain[];

/**
 * Domains without listings in https://github.com/forcedotcom/sf-skills/tree/main/skills.
 *
 * This is a temporary presentation-only suppression list. Remove domains as public skills are
 * added, or replace this list when search exposes cached facet counts.
 */
export const SUPPRESSED_SKILL_DOMAINS: ReadonlySet<SkillDomain> = new Set([
    'automotive-cloud',
    'channel-revenue-management',
    'cme',
    'communications',
    'consumer-goods',
    'crm-analytics',
    'education-cloud',
    'energy-and-utilities',
    'field-service',
    'fsc',
    'health-cloud',
    'industries',
    'insurance',
    'life-sciences',
    'manufacturing',
    'marketing',
    'net-zero',
    'non-profit',
    'public-sector',
    'revenue',
    'sf-skill',
    'tableau',
    'tableau-next',
]);

/** Domain labels are metadata, never search/listing-detail contract values. */
export const getSkillDomainDisplayName = (domain: SkillDomain): string => SKILL_DOMAIN_DISPLAY_NAMES[domain];

const getDefaultDomain = (): SiteDomain => {
    return typeof window === 'undefined' ? 'appexchange' : (window.stores?.SITE_CONFIG?.domain ?? 'appexchange');
};

/** Existing Skill/Plugin listing-detail presentation remains on AppExchange English. */
export const isExternalAgentExperienceAvailable = (domain = getDefaultDomain()): boolean => {
    // SITE_CONFIG is synchronously injected before client modules load. Match routerStore's
    // existing absent-config behavior by treating SSR/test harnesses as AppExchange.
    return (
        FeatureFlags.FF_DISCOVERY_SKILLS_LISTINGS &&
        domain === 'appexchange' &&
        getUserLanguage() === ENGLISH_LANGUAGE_CODE
    );
};

/** External Agents search is shared by both English marketplace domains. */
export const isExternalAgentSearchExperienceAvailable = (domain = getDefaultDomain()): boolean =>
    FeatureFlags.FF_DISCOVERY_SKILLS_LISTINGS &&
    (domain === 'appexchange' || domain === 'agentexchange') &&
    getUserLanguage() === ENGLISH_LANGUAGE_CODE;

/** The Skills landing page and its navigation live on AgentExchange English. */
export const isExternalAgentDiscoveryExperienceAvailable = (domain = getDefaultDomain()): boolean => {
    const skillsListingsEnabled = FeatureFlags.FF_DISCOVERY_SKILLS_LISTINGS;
    return skillsListingsEnabled && isSkillsLandingPageAvailable(domain, getUserLanguage(), skillsListingsEnabled);
};

/** Skill/Plugin cards render on both the AgentExchange landing page and AppExchange search. */
export const isExternalAgentCardPresentationAvailable = (domain = getDefaultDomain()): boolean =>
    isExternalAgentDiscoveryExperienceAvailable(domain) || isExternalAgentSearchExperienceAvailable(domain);
